import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@material-ui/core'
import { Controller, useForm } from 'react-hook-form'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../../../commonDesign/Button'
import SydLabel, { hookFormErrorAdapter } from '../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../commonDesign/SydInput'
import SydSelect from '../../../../../commonDesign/SydSelect'
import { provisionedTargets, useListIntegrationDomains } from '../../../../../../api/integrations'
import { decipherError } from '../../../../../../utils/decipherError'

function CreateProvisionedTargetForm ({ onComplete, onCancel }) {
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const { mutateAsync: createTarget } = provisionedTargets.useCreateProvisionedTargetTable()
  const { data: domainsData } = useListIntegrationDomains()

  const domainOptions = useMemo(() => {
    const domains = domainsData?.integrationDomains || []
    return [
      { label: '— None —', value: '' },
      ...domains.map(d => ({ label: d.domain, value: d.domain }))
    ]
  }, [domainsData])

  const form = useForm({
    defaultValues: {
      tableName: '',
      domain: '',
      description: ''
    },
    mode: 'onChange'
  })

  const handleSubmit = useCallback(async (formData) => {
    const command = {
      tableName: formData.tableName,
      domain: formData.domain || null,
      description: formData.description || null
    }

    try {
      setError(null)
      setProcessing(true)
      const result = await createTarget(command)
      onComplete?.(result)
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [createTarget, onComplete])

  const onSubmit = useCallback(async (e) => {
    const handler = form.handleSubmit(handleSubmit, (errors) => console.error(errors))
    await handler(e)
  }, [form, handleSubmit])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Controller
          control={form.control}
          name='tableName'
          rules={{
            required: 'Table name is required',
            maxLength: { value: 128, message: 'Maximum 128 characters' }
          }}
          render={(f) => (
            <SydLabel
              label='Table Name'
              error={hookFormErrorAdapter(form, f.fieldState)}
            >
              <SydInput {...f.field} placeholder='e.g. redtail_contacts' minWidth='0' />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Controller
          control={form.control}
          name='domain'
          render={(f) => (
            <SydLabel label='Domain'>
              <SydSelect
                minWidth='0'
                options={domainOptions}
                value={f.field.value}
                onChange={f.field.onChange}
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={form.control}
          name='description'
          rules={{
            maxLength: { value: 256, message: 'Maximum 256 characters' }
          }}
          render={(f) => (
            <SydLabel
              label='Description'
              error={hookFormErrorAdapter(form, f.fieldState)}
            >
              <SydInput {...f.field} placeholder='Optional description' minWidth='0' />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <SydModalActions>
          {error ? <div className='__error'>{error}</div> : null}
          <SydButton disabled={processing} variant='ghost' size='md' onClick={onCancel}>
            Cancel
          </SydButton>
          <SydButton
            processing={processing}
            disabled={!form.formState.isValid}
            variant='primary'
            size='md'
            onClick={onSubmit}
          >
            Create
          </SydButton>
        </SydModalActions>
      </Grid>
    </Grid>
  )
}

CreateProvisionedTargetForm.propTypes = {
  onComplete: PropTypes.func,
  onCancel: PropTypes.func
}

export default CreateProvisionedTargetForm
